export type Role = 'rv_admin' | 'org_admin' | 'export' | 'viewer'

export interface Organization {
  id: string
  name: string
  mi_enabled: boolean
  created_at: string
}

export interface OrgMember {
  id: string
  user_id: string
  org_id: string
  role: Role
  created_at: string
}

export interface Site {
  id: string
  org_id: string
  name: string
  droplet_ip: string | null
  go2rtc_port: number | null
  group_name: string | null
  latitude: number | null
  longitude: number | null
  created_at: string
}

export interface Gateway {
  id: string
  site_id: string
  name: string
  droplet_ip: string
  go2rtc_port: number
  router_ip: string | null
  tunnel_ip: string | null
  carrier: string | null
  sim_only: boolean
  rssi: number | null
  rsrp: number | null
  rsrq: number | null
  sinr: number | null
  created_at: string
}

export interface Camera {
  id: string
  site_id: string
  gateway_id: string | null
  name: string
  cam_number: number
  cam_ip: string
  port: number
  external_ip: string | null
  rtsp_url: string
  stream_name: string
  rtsp_path: string
  username: string
  password: string
  latitude: number | null
  longitude: number | null
  created_at: string
}

export interface StreamInfo {
  wsUrl: string
  username: string
  password: string
  channelId: number
}

export interface OrgMembership {
  org_id: string
  org_name: string
  role: Role
  mi_enabled: boolean
}

export interface UserProfile {
  id: string
  email: string
  role: Role
  org_id: string | null
  org_name: string | null
  mi_enabled: boolean
  memberships: OrgMembership[]
  provider?: string
}

// ── Camera Capabilities ──

/** Intelligence analysis feature IDs returned by getSupportIntelligences */
export const IntelligenceType = {
  Perimeter: 0,
  SmartMotion: 1,
  SafetyHat: 2,
  SafetyVest: 3,
  TripWire: 7,
  MultiTripWire: 8,
  Loitering: 9,
  ObjectLeft: 11,
  ObjectMoved: 12,
  AbnormalSpeed: 13,
  Converse: 14,
  NoParking: 15,
  Fence: 17,
  EnterArea: 20,
  LeaveArea: 21,
  IntelligenceTrace: 22,
  PeopleCounting: 23,
  BoatDetect: 26,
  SmokingDetect: 28,
  FireSmokeDetect: 29,
  SmallFireDetect: 30,
} as const
export type IntelligenceType = (typeof IntelligenceType)[keyof typeof IntelligenceType]

export interface CameraCapabilities {
  ptz: boolean
  presetMaxNum: number
  intelligences: IntelligenceType[]
  deviceModel?: string
  firmwareVersion?: string
}

export interface Point {
  x: number
  y: number
}

export interface DetectionRegion {
  id: number
  enabled: boolean
  points: Point[]
}

export interface MotionZone {
  topX: number
  topY: number
  width: number
  height: number
}

export interface MotionDetectionConfig {
  enabled: boolean
  sensitivity: number
  zones: MotionZone[]
}

export interface PerimeterConfig {
  enabled: boolean
  sensitivity: number
  targetType: number // 0=all, 1=person, 2=car
  regions: DetectionRegion[]
}

export interface TripWireConfig {
  enabled: boolean
  targetType: number
  lines: { id: number; startX: number; startY: number; endX: number; endY: number }[]
}

export interface AreaDetectionConfig {
  enabled: boolean
  targetType: number
  regions: DetectionRegion[]
}

export interface PeopleCountingConfig {
  enabled: boolean
  osdEnabled: boolean
  regionType: number // 1=line, 2=rectangle
  alarmEnabled: boolean
  alarmThreshold: number
  line?: { startX: number; startY: number; endX: number; endY: number }
}

export interface PrivacyMask {
  areaId: number
  enabled: boolean
  topX: number
  topY: number
  width: number
  height: number
  blindType: number // 1=color, 2=mosaic, 3=both
  blindColor: string
}

// ── Viewer Layouts ──

export type LayoutId = '1x1' | '1+5' | '2x2' | '1+7' | '3x3' | '4x4' | '5x5' | '6x6'

export interface LayoutPreset {
  id: LayoutId
  label: string
  cols: number
  rows: number
  totalSlots: number
  /** CSS grid placement per slot index. If absent, auto-flow is used (square grids). */
  slotStyles?: Record<number, { gridColumn: string; gridRow: string }>
}

export const LAYOUT_PRESETS: LayoutPreset[] = [
  { id: '1x1', label: '1x1', cols: 1, rows: 1, totalSlots: 1 },
  {
    id: '1+5', label: '1+5', cols: 3, rows: 3, totalSlots: 6,
    slotStyles: {
      0: { gridColumn: '1 / 3', gridRow: '1 / 3' },
      1: { gridColumn: '3 / 4', gridRow: '1 / 2' },
      2: { gridColumn: '3 / 4', gridRow: '2 / 3' },
      3: { gridColumn: '1 / 2', gridRow: '3 / 4' },
      4: { gridColumn: '2 / 3', gridRow: '3 / 4' },
      5: { gridColumn: '3 / 4', gridRow: '3 / 4' },
    },
  },
  { id: '2x2', label: '2x2', cols: 2, rows: 2, totalSlots: 4 },
  {
    id: '1+7', label: '1+7', cols: 4, rows: 4, totalSlots: 8,
    slotStyles: {
      0: { gridColumn: '1 / 4', gridRow: '1 / 4' },
      1: { gridColumn: '4 / 5', gridRow: '1 / 2' },
      2: { gridColumn: '4 / 5', gridRow: '2 / 3' },
      3: { gridColumn: '4 / 5', gridRow: '3 / 4' },
      4: { gridColumn: '1 / 2', gridRow: '4 / 5' },
      5: { gridColumn: '2 / 3', gridRow: '4 / 5' },
      6: { gridColumn: '3 / 4', gridRow: '4 / 5' },
      7: { gridColumn: '4 / 5', gridRow: '4 / 5' },
    },
  },
  { id: '3x3', label: '3x3', cols: 3, rows: 3, totalSlots: 9 },
  { id: '4x4', label: '4x4', cols: 4, rows: 4, totalSlots: 16 },
  { id: '5x5', label: '5x5', cols: 5, rows: 5, totalSlots: 25 },
  { id: '6x6', label: '6x6', cols: 6, rows: 6, totalSlots: 36 },
]

export const LAYOUT_MAP = Object.fromEntries(LAYOUT_PRESETS.map(p => [p.id, p])) as Record<LayoutId, LayoutPreset>
export const MAX_SLOTS = 36

// Back-compat alias
export type GridSize = LayoutId

export interface ViewerLayout {
  id: string
  name: string
  org_id: string
  created_by: string
  site_id: string | null
  group_name: string | null
  visibility: 'personal' | 'org'
  grid_size: LayoutId
  slots: (string | null)[]
  created_at: string
  updated_at: string
}

export interface ViewerLayoutDefault {
  id: string
  site_id: string
  layout_id: string
  user_id: string | null  // null = org-level default
  created_at: string
}

// ── Camera Events & MI ──

export type EventType = 'face' | 'body' | 'license_plate' | 'vehicle' | 'motion' | 'intrusion' | 'line_cross' | 'enter_area' | 'leave_area'

export interface CameraEvent {
  id: string
  camera_id: string
  site_id: string
  org_id: string
  event_type: EventType
  alarm_type: number | null
  timestamp: string
  confidence: number | null
  metadata: Record<string, unknown>
  has_snapshot: boolean
  snapshot_url: string | null
  created_at: string
}

// ── Camera Health ──

export type HealthStatus = 'online' | 'offline' | 'degraded' | 'unknown'

export interface CameraHealth {
  id: string
  camera_id: string
  org_id: string
  status: HealthStatus
  last_seen_at: string | null
  device_model: string | null
  firmware_version: string | null
  uptime_seconds: number | null
  error_message: string | null
  checked_at: string
}

// ── Audit Logs ──

export interface AuditLog {
  id: string
  org_id: string
  user_id: string | null
  user_email: string | null
  action: string
  entity_type: string
  entity_id: string | null
  entity_name: string | null
  details: Record<string, unknown>
  ip_address: string | null
  created_at: string
}

// ── Notifications ──

export type NotificationSeverity = 'info' | 'warning' | 'critical'

export interface Notification {
  id: string
  org_id: string
  user_id: string | null
  title: string
  body: string | null
  severity: NotificationSeverity
  source_type: string | null
  source_id: string | null
  read_at: string | null
  created_at: string
}

export interface PeopleCountingStat {
  id: string
  camera_id: string
  site_id: string
  org_id: string
  period_start: string
  period_type: 'hour' | 'day' | 'month'
  enter_count: number
  exit_count: number
  created_at: string
}

